import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "plumber-web-design-colorado",
  canonicalOverride: "https://coloradowebimpressions.com/plumbing-web-design-colorado",
  metaTitle: "Plumbing Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom plumbing web design across Colorado — fast, mobile-first, conversion-tested sites built for emergency calls. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Plumbing marketing", to: "/plumbing-marketing-colorado" },
  ],
  currentLabel: "Plumbing Web Design Colorado",
  eyebrow: "Plumbing Web Design — Colorado",
  h1: (
    <>
      Plumbing <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded plumbing websites that load in under 2 seconds, convert mobile emergency searches, and rank from day one.",
  intro:
    "Plumbing websites get judged in 4 seconds at 11 p.m. on a phone with one bar of service. We hand-code fast, mobile-first plumbing sites with sticky click-to-call, after-hours messaging, dedicated service pages for every job type, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns emergency searches into booked jobs.",
  definition: {
    term: "Plumbing web design in Colorado",
    body: "Plumbing web design is the practice of building conversion-focused, mobile-first websites for plumbing companies — with dedicated service pages, click-to-call CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second load on mobile, scoring 95+ on Core Web Vitals — no bloated themes or page builders." },
    { title: "One page per service line", body: "Water heater, drain cleaning, sewer line, slab leak, re-pipe, gas line — each ranks independently." },
    { title: "Sticky click-to-call", body: "The phone number stays visible on every scroll, on every page, on every device." },
    { title: "Service area pages", body: "Dedicated pages for every Colorado city you serve — proper schema, unique content, real photos." },
    { title: "Booking & estimate forms", body: "Conversion-tested forms with the minimum fields needed to dispatch a truck." },
    { title: "Schema and SEO baked in", body: "LocalBusiness, Service, FAQPage, Review schema on every page from launch — no SEO retrofit needed later." },
  ],
  whyThisMarket: [
    {
      heading: "Mobile speed wins or loses the call",
      body: "80%+ of plumbing searches in Colorado happen on mobile, and 60%+ are emergency-intent. A 4-second load time means you lost the call to the next plumber in the Map Pack.",
    },
    {
      heading: "Most plumbing sites are template-built and slow",
      body: "We've audited hundreds of Colorado plumbing sites — the vast majority are built on Wix, GoDaddy, or off-the-shelf themes that score under 30 on PageSpeed. Hand-coded sites blow past them by default.",
    },
    {
      heading: "Service pages drive ranking, not the homepage",
      body: "Most plumbing sites bury their services in dropdown menus. We build full ranking pages for every service line so each one earns its own organic traffic and Map Pack association.",
    },
  ],
  relatedLinks: [
    { to: "/plumbing-marketing-colorado", label: "Plumbing marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/plumbing-seo-colorado", label: "Plumbing SEO in Colorado" },
    { to: "/plumbing-google-maps-colorado", label: "Plumbing Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado plumbing case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a plumbing website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a plumbing website?", a: "Typical timeline is 4–6 weeks for a 12–20 page plumbing site with full service and service-area pages. Larger multi-location plumbing companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs plumbers?", a: "No — we build plumbing sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Plumbing Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for plumbing companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/plumber-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
